import { useState } from 'react';
import { notify } from '../../../app/Notifications';
import { useRequest } from '../../../hooks';
import {
  getAgentWidgetSettings,
  getFormByName,
  updateAgentWidgetSettings,
  uploadAgentMedia,
} from '../../../api';
import { filterDataBySchema } from '../utils/filterByJsonSchema';
import { getDefaultSettingsValue } from '../utils/getDefaultSettings';
import type {
  AgentWidgetSettings,
  AgentWidgetSettingsRequestBody,
  FormJsonData,
} from '../../../api';

export const useAgentWidgetSettings = () => {
  const [filteredWidgetData, setFilteredWidgetData] =
    useState<AgentWidgetSettings | null>(null);
  const [schema, setSchema] = useState<FormJsonData | null>(null);

  const [getWidgetSettings, initializing] = useRequest(
    (_signal, agentId: string) => {
      return getAgentWidgetSettings(agentId);
    },
    {
      onSuccess: async (data: AgentWidgetSettings) => {
        if (!data) return;

        const schema = await getFormByName('agentWidgetSettings');
        const filteredData = data.widgetSettings
          ? filterDataBySchema(
              data.widgetSettings as AgentWidgetSettings,
              (schema as FormJsonData).jsonSchema
            )
          : {};

        const defaultSettingsData = getDefaultSettingsValue(
          (schema as FormJsonData).jsonSchema.properties
        );

        setSchema(schema as FormJsonData);
        setFilteredWidgetData(prev => ({
          ...defaultSettingsData,
          ...filteredData,
        }));
      },
      onError: () => {
        notify.error('Failed to retrieve widget settings');
      },
    }
  );

  const [handleUpdateWidgetSettings, updating] = useRequest(
    (_signal, agentId: string, data: AgentWidgetSettingsRequestBody) => {
      return updateAgentWidgetSettings(agentId, data);
    },
    {
      onSuccess: async (data: AgentWidgetSettings) => {
        if (!data) return;

        const schema = await getFormByName('agentWidgetSettings');
        const filteredData = data.widgetSettings
          ? filterDataBySchema(
              data.widgetSettings as AgentWidgetSettings,
              (schema as FormJsonData).jsonSchema
            )
          : {};

        setSchema(schema as FormJsonData);
        setFilteredWidgetData(filteredData);
        notify.success('Widget settings updated successfully');
      },
      onError: () => {
        notify.error('Failed to update widget settings');
      },
    }
  );
  const [handleUploadMedia, uploading] = useRequest(
    (_signal, agentId: string, file: Blob) => {
      return uploadAgentMedia({ file }, agentId);
    },
    {
      onSuccess: () => {
        notify.success('Media uploaded successfully');
      },
      onError: () => {
        notify.error('Failed to upload media');
      },
    }
  );

  return {
    data: filteredWidgetData,
    isLoading: initializing || updating,
    schema,
    handleUpdateWidgetSettings,
    getWidgetSettings,
    setWidgetData: setFilteredWidgetData,
    handleUploadMedia,
    uploading,
  };
};
